import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  FileText,
  Upload,
  CreditCard,
  Coins,
  BarChart3,
  Settings,
  Users,
  Megaphone,
  Building2,
  Landmark,
  LogOut,
  Sun,
  Moon,
  Loader2,
  Menu,
  X,
  Scale,
  Sparkles,
  UserCircle,
  HandCoins,
  BookOpen,
  FileSearch,
} from 'lucide-react';
import { useAuthStore, useIsSuperAdmin } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';
import { useImportJobStore } from '../stores/importJobStore';
import { queryClient } from '../lib/queryClient';
import { useTenantLogo } from '../lib/useTenantLogo';
import api from '../lib/axios';

const NAV_GROUPS = [
  {
    titleKey: 'nav.groupOperations',
    items: [
      { key: 'dashboard', to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard, end: true },
      { key: 'entries', to: '/entries', labelKey: 'nav.entries', icon: FileText },
    ],
  },
  {
    titleKey: 'nav.groupTreasury',
    items: [
      { key: 'checks', to: '/checks', labelKey: 'nav.checks', icon: CreditCard },
      { key: 'cash', to: '/cash', labelKey: 'nav.cash', icon: Coins },
      { key: 'imports', to: '/imports', labelKey: 'nav.imports', icon: Upload },
      { key: 'reconciliation', to: '/reconciliation', labelKey: 'nav.reconciliation', icon: Scale },
    ],
  },
  {
    titleKey: 'nav.groupTracking',
    items: [
      { key: 'contacts', to: '/contacts', labelKey: 'nav.contacts', icon: Users },
      { key: 'cotisants', to: '/cotisants', labelKey: 'nav.cotisants', icon: HandCoins },
      { key: 'campaigns', to: '/campaigns', labelKey: 'nav.campaigns', icon: Megaphone },
      { key: 'assets', to: '/assets', labelKey: 'nav.assets', icon: Landmark },
      { key: 'reports', to: '/reports', labelKey: 'nav.reports', icon: BarChart3 },
    ],
  },
  {
    titleKey: 'nav.groupConfig',
    items: [
      { key: 'settings', to: '/settings', labelKey: 'nav.settings', icon: Settings },
    ],
  },
];

// Menu entries an admin may hide; 'settings' stays visible so the admin keeps access.
export const HIDEABLE_NAV: { key: string; labelKey: string }[] = NAV_GROUPS
  .flatMap((g) => g.items)
  .filter((i) => i.key !== 'settings')
  .map((i) => ({ key: i.key, labelKey: i.labelKey }));

const PLATFORM_GROUP = {
  titleKey: 'nav.groupPlatform',
  items: [
    { to: '/tenants', labelKey: 'nav.tenants', icon: Building2, end: false },
    { to: '/ai-costs', labelKey: 'nav.aiCosts', icon: Sparkles, end: false },
    { to: '/bank-profiles', labelKey: 'nav.bankProfiles', icon: FileSearch, end: false },
  ],
};

export default function Layout() {
  const { t } = useTranslation();
  const { tenant, tenants, user, logout, selectTenant } = useAuthStore();
  const isSuperAdmin = useIsSuperAdmin();
  const { theme, toggle } = useThemeStore();
  const navigate = useNavigate();
  const importAnalyzing = useImportJobStore((s) => s.job?.status === 'analyzing');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const navVisibility = useQuery<{ hidden: string[] }>({
    queryKey: ['nav-visibility'],
    queryFn: () => api.get('/settings/navigation').then((r) => r.data),
    enabled: !!tenant,
    staleTime: 5 * 60_000,
  });
  const hidden = navVisibility.data?.hidden ?? [];

  const { data: logoUrl } = useTenantLogo();

  // A tenant-less super-admin only sees the platform back-office links.
  const baseGroups = !tenant && isSuperAdmin
    ? [PLATFORM_GROUP]
    : isSuperAdmin
      ? [...NAV_GROUPS, PLATFORM_GROUP]
      : NAV_GROUPS;

  // Drop admin-hidden entries (settings is never hideable), then drop now-empty groups.
  const navGroups = baseGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((i) => !('key' in i) || !hidden.includes((i as { key: string }).key)),
    }))
    .filter((group) => group.items.length > 0);

  const onLogout = () => {
    logout();
    navigate('/login');
  };

  const onSwitchTenant = async (slug: string) => {
    if (!slug || slug === tenant?.slug || switching) return;
    setSwitching(true);
    try {
      await selectTenant(slug);
      queryClient.clear();
      setDrawerOpen(false);
      navigate('/');
    } finally {
      setSwitching(false);
    }
  };

  const initials = (tenant?.name ?? 'Hissab').trim().charAt(0).toUpperCase();

  const brandBlock = (
    <div className="flex items-center gap-3 px-5 py-5">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={tenant?.name ?? t('common.appName')}
          className="h-9 w-9 shrink-0 rounded-lg object-contain"
        />
      ) : (
        <span
          className="grid h-9 w-9 place-items-center rounded-lg bg-brand font-display text-lg font-semibold text-brand-contrast"
          aria-hidden
        >
          {initials}
        </span>
      )}
      <div className="min-w-0">
        <div className="font-display text-lg font-semibold leading-none text-ink">
          {t('common.appName')}
        </div>
        <div className="mt-1 truncate text-xs text-ink-muted">{tenant?.name}</div>
      </div>
    </div>
  );

  const navLinks = (
    <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-2">
      {navGroups.map((group) => (
        <div key={group.titleKey} className="space-y-0.5">
          <div className="px-3 pb-1 pt-2 text-[0.65rem] font-semibold uppercase tracking-wider text-ink-muted">
            {t(group.titleKey)}
          </div>
          {group.items.map(({ to, labelKey, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setDrawerOpen(false)}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-tint text-brand-strong'
                    : 'text-ink-soft hover:bg-surface-raised hover:text-ink'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`absolute left-0 h-5 w-[3px] rounded-full bg-brand transition-opacity ${
                      isActive ? 'opacity-100' : 'opacity-0'
                    }`}
                    aria-hidden
                  />
                  <Icon
                    size={18}
                    className={isActive ? 'text-brand-strong' : 'text-ink-muted group-hover:text-ink'}
                  />
                  {t(labelKey)}
                  {to === '/imports' && importAnalyzing && (
                    <Loader2
                      size={14}
                      className="ml-auto animate-spin text-brand"
                      aria-label={t('imports.inProgress')}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );

  const footerBlock = (
    <div className="border-t border-line px-3 py-3">
      {tenants.length > 1 && tenant && (
        <div className="mb-2 px-2">
          <label className="mb-1 block text-[0.65rem] font-semibold uppercase tracking-wider text-ink-muted">
            {t('selectTenant.switch')}
          </label>
          <select
            value={tenant.slug}
            onChange={(e) => onSwitchTenant(e.target.value)}
            disabled={switching}
            className="input !py-1.5 text-sm"
            aria-label={t('selectTenant.switch')}
          >
            {tenants.map((org) => (
              <option key={org.slug} value={org.slug}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 px-2">
        <NavLink
          to="/profile"
          onClick={() => setDrawerOpen(false)}
          className={({ isActive }) =>
            `flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${
              isActive ? 'bg-brand-tint text-brand-strong' : 'text-ink-soft hover:bg-surface-raised'
            }`
          }
          title={t('profile.title')}
        >
          <UserCircle size={18} className="shrink-0 text-ink-muted" />
          <span className="min-w-0 truncate text-xs">
            <span className="block truncate font-medium text-ink">{user?.name || t('profile.title')}</span>
            <span className="block truncate text-ink-muted">{user?.email}</span>
          </span>
        </NavLink>
        <button
          type="button"
          onClick={toggle}
          className="btn btn-ghost !px-2 !py-2"
          aria-label={t('common.toggleTheme')}
          title={t('common.toggleTheme')}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>
      </div>
      <a
        href="/guide"
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-ghost mt-1 w-full justify-start !px-2"
      >
        <BookOpen size={16} /> {t('nav.userGuide')}
      </a>
      <button onClick={onLogout} className="btn btn-ghost mt-1 w-full justify-start !px-2">
        <LogOut size={16} /> {t('common.logout')}
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-paper text-ink">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-line bg-surface lg:flex">
        {brandBlock}
        {navLinks}
        {footerBlock}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[85%] flex-col border-r border-line bg-surface shadow-xl">
            <div className="flex items-center justify-between pr-3">
              {brandBlock}
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="btn btn-ghost !px-2 !py-2"
                aria-label={t('common.close')}
              >
                <X size={18} />
              </button>
            </div>
            {navLinks}
            {footerBlock}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header
          className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-surface/90 px-4 py-3 backdrop-blur lg:hidden"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="btn btn-ghost !px-2 !py-2"
            aria-label={t('common.menu')}
          >
            <Menu size={20} />
          </button>
          <span
            className="grid h-8 w-8 place-items-center rounded-lg bg-brand font-display text-base font-semibold text-brand-contrast"
            aria-hidden
          >
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-base font-semibold leading-none text-ink">
              {tenant?.name ?? t('common.appName')}
            </div>
          </div>
          <button
            type="button"
            onClick={toggle}
            className="btn btn-ghost !px-2 !py-2"
            aria-label={t('common.toggleTheme')}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="app-content mx-auto min-w-0 max-w-6xl px-4 py-6 sm:px-6 lg:px-10 lg:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

